// Imports for external libraries go here.
import { FC, useRef, useEffect, useState } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { RichText, Icon, Button } from '@marriott/mi-ui-library';
import { dataJson } from '../CardComparisonAccordion/CardComparisonAccordion.types';
import { DataCellProps, TableRowProps } from './DataCellComponent.types';
import { StyledDataCell } from './DataCellComponent.styles';
import { IconType, DataType } from '../../modules/utils';
import { CobrandImage } from '../CobrandImage/CobrandImage';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useSession } from '@marriott/mi-headless-utils';
import { isAuthenticated } from '@marriott/mi-ui-library';

export const DataCellConfig = {
  emptyLabel: 'DataCell',
  isEmpty: () => true,
  resourceType: `/components/content/datacell`,
};

// Use named rather than default exports.
export const DataCellComp: FC<DataCellProps> = (props: DataCellProps) => {
  const {
    cardType,
    styleclass,
    categoryIconPath,
    imagePath,
    altText,
    editAltTextCheck,
    editedImgAltText,
    isAuthorMode,
    categoryId,
    groupid,
    eliteStatus,
    primaryCTAText,
    primaryCtaURL,
    openInNewTabPrimary,
    dataType,
    iconType,
    componentId,
    eliteStatusColor,
    trackingProperties,
    pricingTermsSignedIn = '',
    pricingTermsGuest = '',
  } = props;
  const { datalayer } = useSession();
  const [pricingUrl, setPricingUrl] = useState<string>(pricingTermsGuest);
  const linkType = openInNewTabPrimary ? 'external' : 'internal';
  const headerText = props.cardSummary || props.categoryHeading;
  const isHeader = props.type === DataType.Header ? true : false;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (datalayer) {
      setPricingUrl(isAuthenticated() ? pricingTermsSignedIn : pricingTermsGuest);
    }
  }, [datalayer]);
  return (
    <StyledDataCell className={clsx(styleclass)} id={componentId}>
      <div className={clsx('d-flex flex-column cell-wrapper', isAuthorMode ? 'cell-border mb-2' : '')}>
        {isHeader && eliteStatus && (
          <div className={clsx('py-3 px-4 badge', eliteStatusColor + '-status')}>
            <RichText componentId={'elite-status'} customClass={'t-font-inverse-s'} text={eliteStatus} />
          </div>
        )}
        {cardType && isHeader && (
          <div className="d-flex justify-content-end">
            {cardType === 'none' ? (
              <div className={clsx('pt-lg-3 mt-lg-2 pb-lg-1')}></div>
            ) : (
              <span
                data-component-name="a-ui-library-Badge"
                data-testid="card-compare-cardtype-Badge"
                className={clsx(
                  cardType === 'personal' && 'bg-personal',
                  cardType === 'business' && 'bg-business',
                  'm-badge-inline t-overline-normal p-2'
                )}
              >
                {cardType}
              </span>
            )}
          </div>
        )}
        <div className={clsx('p-3', props.categoryHeading ? 'justify-content-flex-start' : 'd-flex flex-grow-1')}>
          {isAuthorMode && categoryId && (
            <div>
              <div> Category ID: {categoryId}</div>
              <div> Group ID: {groupid}</div>
              {imagePath && headerText && headerText.length && (
                <div className="d-flex flex-column flex-md-row">
                  <CobrandImage
                    defaultImageURL={imagePath}
                    altText={editAltTextCheck ? editedImgAltText : altText}
                    customClass={clsx('card-image pr-3')}
                  />
                  <RichText componentId={`data-cell`} text={headerText} />
                </div>
              )}
              {!imagePath && headerText && headerText.length && (
                <div className="d-flex ">
                  {categoryIconPath && categoryIconPath.length && (
                    <div className={clsx('mr-2')}>
                      <Icon iconClass={`${categoryIconPath} icon-l`} />
                    </div>
                  )}
                  <RichText componentId={`data-cell`} text={headerText} />
                </div>
              )}
            </div>
          )}
          {!isAuthorMode && (
            <div className={clsx(headerText ? 'd-flex flex-grow-1' : '')}>
              {isHeader && headerText && headerText.length && (
                <div className="d-flex flex-column justify-content-between flex-grow-1">
                  <div className="d-flex flex-column flex-lg-row">
                    <CobrandImage
                      defaultImageURL={imagePath}
                      altText={editAltTextCheck ? editedImgAltText : altText}
                      customClass={clsx('card-image pr-3')}
                    />
                    <RichText customClass={clsx('mt-3 mt-lg-0')} componentId={`data-cell`} text={headerText} />
                  </div>
                  {pricingUrl && (
                    <RichText text={pricingUrl} componentId="pricing-terms" customClass=" my-3 tertiary-links" />
                  )}
                  <div>
                    {primaryCTAText && (
                      <Button
                        id={`${componentId}-btn`}
                        buttonCopy={primaryCTAText}
                        href={primaryCtaURL}
                        isLink={true}
                        target={openInNewTabPrimary === 'true' ? '_blank' : '_self'}
                        className={'m-button-m'}
                        trackingProperties={trackingProperties}
                        linkType={linkType}
                        ref={ref}
                        linkAriaLabelOpenNewWindow={openInNewTabPrimary ? 'Open in new tab' : ''}
                      ></Button>
                    )}
                  </div>
                </div>
              )}
              {!isHeader && headerText && headerText.length && (
                <div className="d-flex">
                  {categoryIconPath && categoryIconPath.length && (
                    <div className={clsx('mr-2')}>
                      <Icon iconClass={`${categoryIconPath} icon-l`} />
                    </div>
                  )}
                  <RichText componentId={`data-cell`} text={headerText} />
                </div>
              )}
            </div>
          )}
          {dataType === DataType.Icons && iconType && iconType.length && (
            <div
              className={clsx('icon-check-' + IconType[iconType as keyof typeof IconType], 'icon-wrapper text-center')}
            >
              <Icon iconClass={'icon-check icon-inverse'} />
            </div>
          )}
          {dataType === DataType.Emtpy && <div className={'text-left'}> ___</div>}
        </div>
      </div>
    </StyledDataCell>
  );
};

export const DataCell = (props: DataCellProps) => {
  return (
    <EditableComponent config={DataCellConfig} {...props}>
      <DataCellComp {...props} />
    </EditableComponent>
  );
};

export const TableRow = (props: TableRowProps) => {
  const { category, dataList, type, cardIdFromURL, customClass } = props;
  const getColumnClass = () => {
    return cardIdFromURL?.length === 3 ? 'col-4' : 'col-6';
  };
  return (
    <div className={clsx('d-flex flex-wrap flex-column flex-md-row data-row col-12 px-0')}>
      <DataCell
        {...category}
        styleclass={clsx(customClass, 'data-cell category-cell col-12 col-md-4 col-lg-3 px-0')}
        groupid={category.groupId}
      />
      <div className="d-flex col-12 col-md-8 col-lg-9 p-0 data-section">
        {dataList.map((data: dataJson) => (
          <DataCell
            {...data}
            styleclass={clsx('data-cell px-0', getColumnClass())}
            type={type}
            groupid={category.groupId}
          />
        ))}
      </div>
    </div>
  );
};
